// W16 / M16 — Naptárnézet.
//
// Nap / hét / hónap. A tervek kiemelik a sűrű nap kezelését: a harmadik elem
// után „+N további”, ami napnézetre vált — enélkül egy zsúfolt nap széthúzná
// a sort, és a hónap elveszítené az áttekinthetőségét.

import { t } from '../lib/i18n';
import { h, icon } from '../lib/dom';
import { priorityLabel, priorityMeta, type TaskPriority, type TaskView } from '../lib/model';

export type CalendarMode = 'day' | 'week' | 'month';

/// A napok es honapok nevenek KULCSAI. Kesz szoveget nem tarolhat modul
/// szintu tabla: a szotar csak a render elott erkezik meg.
const weekdayKeys = [
  'cal.mon',
  'cal.tue',
  'cal.wed',
  'cal.thu',
  'cal.fri',
  'cal.sat',
  'cal.sun',
];
const monthKeys = [
  'cal.m1', 'cal.m2', 'cal.m3', 'cal.m4', 'cal.m5', 'cal.m6',
  'cal.m7', 'cal.m8', 'cal.m9', 'cal.m10', 'cal.m11', 'cal.m12',
];

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/// A hét hétfővel kezdődik.
export function startOfWeek(date: Date): Date {
  const copy = startOfDay(date);
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return copy;
}

export function startOfMonthGrid(date: Date): Date {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  return startOfWeek(first);
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/// A látható időszak első napja és napjainak száma.
export function rangeOf(mode: CalendarMode, anchor: Date): { from: Date; days: number } {
  switch (mode) {
    case 'day':
      return { from: startOfDay(anchor), days: 1 };
    case 'week':
      return { from: startOfWeek(anchor), days: 7 };
    case 'month':
      // Mindig 6 teljes hét, hogy a rács magassága ne ugráljon hónapváltáskor.
      return { from: startOfMonthGrid(anchor), days: 42 };
  }
}

export function titleOf(mode: CalendarMode, anchor: Date): string {
  if (mode === 'day') {
    return `${anchor.getFullYear()}. ${t(monthKeys[anchor.getMonth()] ?? '')} ${anchor.getDate()}.`;
  }
  if (mode === 'week') {
    const from = startOfWeek(anchor);
    const to = new Date(from);
    to.setDate(to.getDate() + 6);
    const sameMonth = from.getMonth() === to.getMonth();
    return sameMonth
      ? `${from.getFullYear()}. ${t(monthKeys[from.getMonth()] ?? '')} ${from.getDate()}. – ${to.getDate()}.`
      : `${from.getFullYear()}. ${t(monthKeys[from.getMonth()] ?? '')} ${from.getDate()}. – ${t(monthKeys[to.getMonth()] ?? '')} ${to.getDate()}.`;
  }
  return `${anchor.getFullYear()}. ${t(monthKeys[anchor.getMonth()] ?? '')}`;
}

/// Feladatok napra bontva; kulcs az ISO dátum (YYYY-MM-DD).
export function groupByDay(tasks: TaskView[]): Map<string, TaskView[]> {
  const map = new Map<string, TaskView[]>();
  for (const task of tasks) {
    if (!task.deadline) continue;
    const key = startOfDay(task.deadline).toISOString().slice(0, 10);
    const list = map.get(key) ?? [];
    list.push(task);
    map.set(key, list);
  }
  // Napon belül idő szerint.
  for (const list of map.values()) {
    list.sort((a, b) => (a.deadline?.getTime() ?? 0) - (b.deadline?.getTime() ?? 0));
  }
  return map;
}

function timeOf(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/// A prioritás ikonja a naptárelemen — a terv 10 px-es jelet mutat a
/// szöveg előtt, hogy a besorolás ne csak a sávszínből derüljön ki.
const calendarPriorityIcon: Record<TaskPriority, string> = {
  low: 'M4.4 6.4L8 10l3.6-3.6',
  mid: 'M4 8h8',
  high: 'M4.4 9.6L8 6l3.6 3.6',
  urgent: 'M8 3v6M8 12.2v.3',
};

function calendarItem(task: TaskView, onSelect: (task: TaskView) => void): HTMLElement {
  const meta = task.priority ? priorityMeta[task.priority] : undefined;
  return h(
    'button',
    {
      type: 'button',
      class: `cal-item${meta ? ` ${meta.cls}` : ''}`,
      title: [
        task.title,
        task.priority ? priorityLabel(task.priority) : null,
        task.deadline ? timeOf(task.deadline) : null,
      ]
        .filter(Boolean)
        .join(' · '),
      onClick: () => onSelect(task),
    },
    task.priority
      ? h(
          'span',
          { class: 'ic', style: { width: '10px', height: '10px' } },
          icon(calendarPriorityIcon[task.priority], 10),
        )
      : null,
    h('span', { class: 'cal-item-title w6', text: task.title }),
    task.deadline ? h('span', { class: 'mono', text: timeOf(task.deadline) }) : null,
  );
}

/// Olvashato nap-cimke a gomb cimkejehez (kepernyoolvasonak is).
function formatDayLabel(date: Date): string {
  return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}.`;
}

export function calendarPage(options: {
  mode: CalendarMode;
  anchor: Date;
  tasks: TaskView[];
  onModeChange: (mode: CalendarMode) => void;
  onAnchorChange: (anchor: Date) => void;
  onSelect: (task: TaskView) => void;
  /// Feladat felvétele egy adott napra.
  ///
  /// A naptár eddig CSAK olvasható volt: a napra kattintás semmit nem
  /// indított, pedig épp ott dől el, mikorra kell a munka.
  onAddOnDay?: (date: Date) => void;
}): HTMLElement {
  const { mode, anchor } = options;
  const { from, days } = rangeOf(mode, anchor);
  const byDay = groupByDay(options.tasks);
  const today = new Date();

  const step = (direction: -1 | 1) => {
    const next = new Date(anchor);
    if (mode === 'day') next.setDate(next.getDate() + direction);
    else if (mode === 'week') next.setDate(next.getDate() + 7 * direction);
    else next.setMonth(next.getMonth() + direction);
    options.onAnchorChange(next);
  };

  const periodLabel = t(
    { day: 'cal.unitDay', week: 'cal.unitWeek', month: 'cal.unitMonth' }[mode],
  );

  /// A terv sorrendje: ‹ · időszak címe · › · Ma, majd jobbra a
  /// prioritás-jelmagyarázat. A nézetváltó a címsor elé kerül, mert az
  /// dönti el, MIT jelent a nyíl egy lépése.
  const toolbar = h(
    'div',
    {
      class: 'row g12',
      style: {
        alignItems: 'center',
        padding: '12px 20px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        flexWrap: 'wrap',
      },
    },
    h(
      'div',
      { class: 'seg' },
      ...(['day', 'week', 'month'] as CalendarMode[]).map((m) =>
        h('button', {
          type: 'button',
          class: m === mode ? 'on' : '',
          'aria-pressed': m === mode ? 'true' : 'false',
          text: t({ day: 'cal.viewDay', week: 'cal.viewWeek', month: 'cal.viewMonth' }[m]),
          onClick: () => options.onModeChange(m),
        }),
      ),
    ),
    h('button', {
      class: 'btn btn-ghost btn-icon btn-sm',
      type: 'button',
      'aria-label': t('cal.previous', [periodLabel]),
      text: '‹',
      onClick: () => step(-1),
    }),
    h('span', { class: 'h3', text: titleOf(mode, anchor) }),
    h('button', {
      class: 'btn btn-ghost btn-icon btn-sm',
      type: 'button',
      'aria-label': t('cal.next', [periodLabel]),
      text: '›',
      onClick: () => step(1),
    }),
    h('button', {
      class: 'btn btn-secondary btn-sm',
      type: 'button',
      text: 'Ma',
      onClick: () => options.onAnchorChange(new Date()),
    }),
    // Jelmagyarazat: a sav szine onmagaban nem mondja meg, mit jelent.
    // Ez eddig sehol nem szerepelt a webes naptaron.
    h(
      'div',
      { class: 'row g8', style: { marginLeft: 'auto', alignItems: 'center', flexWrap: 'wrap' } },
      ...(Object.keys(priorityMeta) as TaskPriority[]).map((priority) =>
        h(
          'span',
          { class: `prio ${priorityMeta[priority].cls}` },
          h(
            'span',
            { class: 'ic', style: { width: '12px', height: '12px' } },
            icon(calendarPriorityIcon[priority], 12),
          ),
          h('span', { text: priorityLabel(priority) }),
        ),
      ),
    ),
  );

  const cells: HTMLElement[] = [];
  for (let index = 0; index < days; index += 1) {
    const date = new Date(from);
    date.setDate(date.getDate() + index);
    const key = startOfDay(date).toISOString().slice(0, 10);
    const list = byDay.get(key) ?? [];

    // Napnezetben minden elem elfer; het es honap nezetben harom utan osszevonunk.
    const limit = mode === 'day' ? list.length : 3;
    const shown = list.slice(0, limit);
    const rest = list.length - shown.length;

    cells.push(
      h(
        'div',
        {
          class: `cal-cell${sameDay(date, today) ? ' today' : ''}${
            mode === 'month' && date.getMonth() !== anchor.getMonth() ? ' other-month' : ''
          }`,
        },
        h(
          'div',
          { class: 'row between' },
          h('span', { class: 'mono cal-daynum', text: `${date.getDate()}.` }),
          // Felvetel erre a napra. A nap hatterere kattintani megtevesztő
          // lenne: a cellaban feladatok is vannak, es egy felrekattintas
          // urlapot nyitna. Sajat, cimkezett gomb egyertelmubb.
          options.onAddOnDay
            ? h('button', {
                class: 'btn btn-ghost btn-icon btn-sm cal-add',
                type: 'button',
                'aria-label': t('cal.addOnDay', [formatDayLabel(date)]),
                title: t('cal.addOnDay', [formatDayLabel(date)]),
                text: '+',
                onClick: () => {
                  // Alapertelmezett idopont 9:00 — a nap eleje ertelmetlen
                  // hatarido lenne, es a felhasznalo ugyis atirhatja.
                  const at = new Date(date);
                  at.setHours(9, 0, 0, 0);
                  options.onAddOnDay?.(at);
                },
              })
            : null,
          // Honap- es hetnezetben a fejlecsor mar kiirja a nap nevet; csak
          // napnezetben (es mobilon, ahol a fejlec elrejtozik) kell ide.
          mode === 'day'
            ? h('span', { class: 'caption tm', text: t(weekdayKeys[(date.getDay() + 6) % 7] ?? '') })
            : h('span', {
                class: 'caption tm hide-desktop',
                text: t(weekdayKeys[(date.getDay() + 6) % 7] ?? ''),
              }),
        ),
        ...shown.map((task) => calendarItem(task, options.onSelect)),
        rest > 0
          ? h('button', {
              type: 'button',
              class: 'caption',
              style: {
                padding: '2px 6px',
                border: '0',
                background: 'none',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                textDecoration: 'underline',
              },
              text: t('cal.moreCount', [String(rest)]),
              onClick: () => {
                // A tervek szerint a tovabbiak napnezetre valtanak.
                options.onAnchorChange(date);
                options.onModeChange('day');
              },
            })
          : null,
      ),
    );
  }

  const headerRow =
    mode === 'month' || mode === 'week'
      ? h(
          'div',
          { class: 'cal-head' },
          ...weekdayKeys.map((key) => h('span', { class: 'label', text: t(key) })),
        )
      : null;

  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    toolbar,
    headerRow,
    h('div', { class: `cal-grid cal-${mode}` }, ...cells),
    h('span', {
      class: 'caption',
      style: { padding: '0 20px 16px' },
      text:
        t('cal.denseDayHint'),
    }),
  );
}
